import { is } from "@callcenter/helpers"

import type { AppModule, AppsCollection } from "./app-initialization.types"

const notifications = () => import(/* webpackChunkName: "Notification" */ "@callcenter-frontend/notification")
const setupHelpers = () => import(/* webpackChunkName: "SetupHelpers" */ "@callcenter-frontend/setup-helpers")

type AppInitializerConstructorOptions = {
  loadAppById?: boolean
  showContentAfterLoadChunk?: boolean
}

const TOKEN_AUTH_APPS = [
  "hubspot-integration",
  "hubspot-integration-phone",
  "freshdesk-integration",
  "salesforce-integration",
  "embedded-omnichannel"
]

export class AppInitializer {
  elements: Array<{ key: string; el: HTMLElement }> = []
  constructor(
    private readonly apps: AppsCollection,
    private readonly options?: AppInitializerConstructorOptions
  ) {
    this.apps = apps
    this.options = options
    this.setElements()
  }
  setElements() {
    if (this.options?.loadAppById) {
      Object.keys(this.apps).forEach((key) => {
        const el = document.getElementById(key)

        if (el) {
          this.elements.push({ key, el })
          window.isAuthByTokenSession = TOKEN_AUTH_APPS.includes(key)
        }
      })
    } else {
      Object.keys(this.apps).forEach((key) => {
        const el = document.querySelector<HTMLElement>(`[data-app="${key}"]`)

        if (el) {
          this.elements.push({ key, el })
        }
      })
    }
  }
  static showContentAfterLoadChunk() {
    const container = document.querySelector<HTMLElement>(".max-1024")
    // To show backend rendered super admin pages after header load
    const adminContainer = document.querySelector<HTMLElement>(".admin-container")

    if (container) {
      container.style.display = "flex"
    }
    if (adminContainer) {
      adminContainer.style.display = "flex"
    }
  }
  static async initSelfService() {
    const { init } = await import("@callcenter-frontend/self-service")

    await init()
  }
  static async showHeaderAfterLoadChunk() {
    if (
      document.querySelector("[data-app=flow-builder]") ||
      document.querySelector("[data-app=flow-builder_old]") ||
      document.querySelector("[data-app=agent-workspace]")
    ) {
      return
    }

    const { initHeader } = await setupHelpers()

    initHeader()
  }
  async initApp({ module, el }: { module: () => Promise<AppModule>; el: HTMLElement }): Promise<void> {
    const { init } = await module()

    init(el)

    if (this.options?.showContentAfterLoadChunk) {
      AppInitializer.showContentAfterLoadChunk()
    }
  }
  async initNotifications() {
    const notificationsElement = document.getElementById("notification-root")

    const systemNotificationsElement = document.querySelector("[data-app='system_notification']")

    if (notificationsElement) {
      const notificationModule = await notifications()

      notificationModule.Notifications(notificationsElement)
    }

    if (systemNotificationsElement && is.html(systemNotificationsElement)) {
      const notificationModule = await notifications()

      notificationModule.SystemNotifications(systemNotificationsElement)
    }
  }
  static async initTosConsent() {
    const tosConsentElement = document.getElementById("tos-consent")

    if (tosConsentElement) {
      const authModule = await import("@callcenter-frontend/authorization")

      authModule.initRegularTosModal(tosConsentElement)
      AppInitializer.showContentAfterLoadChunk()

      return true
    }
  }
  async initTwoFaBlocker() {
    const twoFaBlockerElement = document.getElementById("two-factor-blocker")

    if (twoFaBlockerElement) {
      const { init2faBlocker } = await setupHelpers()

      init2faBlocker.init(twoFaBlockerElement)
      AppInitializer.showContentAfterLoadChunk()

      return true
    }
  }
  async initAll(): Promise<string | undefined> {
    const isTwoFaBlocker = await this.initTwoFaBlocker()

    if (isTwoFaBlocker) {
      return "two_factor_blocker"
    }

    const appModules = this.elements.map(({ key, el }) => this.initApp({ module: this.apps[key], el }))

    await Promise.all(appModules)

    // To show backend rendered super admin pages after header load
    if (appModules.length === 0 && this.options?.showContentAfterLoadChunk) {
      AppInitializer.showContentAfterLoadChunk()
    }

    return undefined
  }
}
